import { useState } from 'react';
import { Alert, Button, Modal, Result, Space, Spin, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { uploadAttachment } from '@/api/attachments';
import { useExtractTopic } from '@/hooks/useExtractTopic';
import { ReviewForm } from './ReviewForm';
import type { ExtractResponse } from '@/types/extractedTopic';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  investorOptions: string[];
}

type Phase = 'pick' | 'uploading' | 'extracting' | 'review' | 'failed';

export function SingleUploadFlow({ open, onClose, onSaved, investorOptions }: Props) {
  const [phase, setPhase] = useState<Phase>('pick');
  const [file, setFile] = useState<UploadFile | null>(null);
  const [attachmentId, setAttachmentId] = useState<string | undefined>();
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const [result, setResult] = useState<ExtractResponse | null>(null);
  const [errMsg, setErrMsg] = useState<string | undefined>();

  const extractMut = useExtractTopic();

  function reset() {
    setPhase('pick');
    setFile(null);
    setAttachmentId(undefined);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(undefined);
    setResult(null);
    setErrMsg(undefined);
  }

  async function run() {
    if (!file?.originFileObj) return;
    setPhase('uploading');
    try {
      const att = await uploadAttachment(file.originFileObj);
      setAttachmentId(att.id);
      setPreviewUrl(URL.createObjectURL(file.originFileObj));
      setPhase('extracting');
      const source = file.type === 'application/pdf' ? 'pdf' : 'image';
      const res = await extractMut.mutateAsync({
        source,
        attachment_id: att.id,
      });
      if (res.status === 'failed') {
        setErrMsg(res.message ?? res.error ?? 'extraction failed');
        setPhase('failed');
      } else {
        setResult(res);
        setPhase('review');
      }
    } catch (e) {
      setErrMsg((e as Error).message);
      setPhase('failed');
    }
  }

  return (
    <Modal
      open={open}
      title="Upload flyer"
      width={phase === 'review' ? 1200 : 600}
      onCancel={() => {
        reset();
        onClose();
      }}
      footer={null}
      destroyOnHidden
    >
      {phase === 'pick' && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Upload.Dragger
            accept=".jpg,.jpeg,.png,.pdf"
            maxCount={1}
            beforeUpload={() => false}
            onChange={(info) => setFile(info.fileList[0] ?? null)}
            fileList={file ? [file] : []}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag flyer (image or PDF, ≤10 MB)</p>
          </Upload.Dragger>
          <Button type="primary" block disabled={!file} onClick={run}>
            Extract
          </Button>
        </Space>
      )}

      {(phase === 'uploading' || phase === 'extracting') && (
        <Result
          icon={<Spin size="large" />}
          title={phase === 'uploading' ? 'Uploading flyer…' : 'Extracting (LLM Vision)…'}
        />
      )}

      {phase === 'failed' && (
        <>
          <Alert type="error" message="Extraction failed" description={errMsg} showIcon />
          <Space style={{ marginTop: 16 }}>
            <Button onClick={reset}>Try again</Button>
            <Button
              onClick={() => {
                reset();
                onClose();
              }}
            >
              Close
            </Button>
          </Space>
        </>
      )}

      {phase === 'review' && result?.draft && (
        <ReviewForm
          draft={result.draft}
          confidence={result.confidence ?? 0}
          warnings={result.warnings ?? []}
          attachmentId={attachmentId}
          flyerPreviewUrl={previewUrl}
          investorOptions={investorOptions}
          onSaved={() => {
            reset();
            onSaved();
            onClose();
          }}
          onDiscard={reset}
        />
      )}
    </Modal>
  );
}
